import { useEffect, useState, useSyncExternalStore } from "react";
import {
  subscribe,
  getInstallState,
  promptInstall,
  isIOS,
} from "../services/pwaInstall";

// Tempo de espera pelo `beforeinstallprompt` nativo antes de assumir que o
// navegador não vai disparar o evento (Brave, Comet e outros baseados em
// Chromium escondem/desativam esse evento, mesmo suportando instalação manual
// via menu). Chrome/Edge disparam quase imediatamente quando a instalabilidade
// é atendida, então essa janela raramente afeta esses navegadores.
const NATIVE_PROMPT_GRACE_MS = 2500;

export interface UsePwaInstall {
  /** Dá para oferecer a instalação (via prompt nativo ou instruções manuais/iOS) e ainda não está instalado. */
  canInstall: boolean;
  /** Há um prompt nativo pronto (Chrome/Edge/Android). */
  canPrompt: boolean;
  /** App já rodando instalado. */
  installed: boolean;
  /** Ambiente iOS/Safari (instalação manual via Compartilhar). */
  isIOS: boolean;
  /** Navegador sem prompt nativo e sem ser iOS (ex.: Brave, Comet) — instalação manual via menu. */
  manualFallback: boolean;
  /** Aciona o prompt nativo (no iOS/manualFallback não faz nada — use as instruções). */
  promptInstall: typeof promptInstall;
}

/**
 * Estado reativo do "instalar como app". Os componentes de UI usam
 * `canInstall` para aparecer/sumir e `promptInstall`/`isIOS`/`manualFallback`
 * para decidir a ação do clique.
 */
export function usePwaInstall(): UsePwaInstall {
  const { canPrompt, installed } = useSyncExternalStore(
    subscribe,
    getInstallState,
    getInstallState,
  );

  // Só oferecemos a instalação após montar no cliente. Isso mantém o primeiro
  // render igual ao HTML pré-renderizado (SSG) e evita mismatch de hidratação
  // — sobretudo no iOS, onde `isIOS()` seria true já no primeiro render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [nativePromptGraceElapsed, setNativePromptGraceElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setNativePromptGraceElapsed(true), NATIVE_PROMPT_GRACE_MS);
    return () => clearTimeout(timer);
  }, []);

  const ios = isIOS();
  const manualFallback = mounted && nativePromptGraceElapsed && !canPrompt && !ios;
  const canInstall = mounted && !installed && (canPrompt || ios || manualFallback);

  return { canInstall, canPrompt, installed, isIOS: ios, manualFallback, promptInstall };
}
